import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { PhilanthropiesAPI, Philanthropy } from '../services/philanthropies.ts';
import {
  formatCurrency,
  applyCurrencyMask,
  parseCurrency,
  formatCurrencyInput,
} from '../utils/currency.ts';
import ConfirmationModal from '../components/ConfirmationModal.tsx';
import DetailShell from '../components/ui/DetailShell.tsx';
import { useAuth } from '../contexts/AuthContext.tsx';
import { formatDateOnly, toDateOnlyString } from '../utils/date.ts';
import { philanthropyCategoryLabel } from '../utils/labels.ts';

const PhilanthropyDetail: React.FC = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();
  const queryClient = useQueryClient();
  const { hasPermission } = useAuth();
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [item, setItem] = useState<Philanthropy | null>(null);
  const [donations, setDonations] = useState<any[]>([]);
  const [donationDate, setDonationDate] = useState('');
  const [donationAmountDisplay, setDonationAmountDisplay] = useState('');
  const [donationDescription, setDonationDescription] = useState('');
  const [savingDonation, setSavingDonation] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingDate, setEditingDate] = useState('');
  const [editingAmountDisplay, setEditingAmountDisplay] = useState('');
  const [editingDescription, setEditingDescription] = useState('');
  const [savingEdit, setSavingEdit] = useState(false);
  const [tab, setTab] = useState<'resumo' | 'doacoes'>('resumo');

  useEffect(() => {
    const load = async () => {
      if (!id) return;
      const data = await PhilanthropiesAPI.get(id);
      setItem(data);
      const rows = await PhilanthropiesAPI.listDonations(id);
      setDonations(rows);
    };
    load();
  }, [id]);

  if (!item) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500" />
      </div>
    );
  }

  const available = Number(item.availableAmount ?? item.amount) || 0;
  const donated = Number(item.donatedAmount || 0);

  return (
    <DetailShell
      breadcrumb={`Patrimônio / Filantropia / ${item.description || 'Detalhe'}`}
      title={item.description || 'Filantropia'}
      subtitle="Detalhe da doação / dotação"
      backLabel="Voltar à filantropia"
      onBack={() => navigate('/philanthropies')}
      actions={
        <>
          {hasPermission('investments.update') && (
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate(`/philanthropies/${item.id}/editar`)}
            >
              Editar
            </button>
          )}
          {hasPermission('assets.delete') && (
            <button type="button" className="btn btn-danger" onClick={() => setShowDeleteModal(true)}>
              Excluir
            </button>
          )}
        </>
      }
      tabs={
        <div className="tabs" style={{ marginBottom: 12 }}>
          <button
            type="button"
            className={`tab${tab === 'resumo' ? ' active' : ''}`}
            onClick={() => setTab('resumo')}
          >
            Resumo
          </button>
          <button
            type="button"
            className={`tab${tab === 'doacoes' ? ' active' : ''}`}
            onClick={() => setTab('doacoes')}
          >
            Doações
          </button>
        </div>
      }
    >
      {tab === 'resumo' ? (
        <>
          <div className="kpi-row" style={{ gridTemplateColumns: 'repeat(3, 1fr)' }}>
            <div className="kpi">
              <div className="kpi-label">Disponível</div>
              <div className="kpi-value">{formatCurrency(available)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Doado</div>
              <div className="kpi-value">{formatCurrency(donated)}</div>
            </div>
            <div className="kpi">
              <div className="kpi-label">Categoria</div>
              <div className="kpi-value" style={{ fontSize: 18 }}>
                {philanthropyCategoryLabel(item.category)}
              </div>
            </div>
          </div>

          <div className="card invest-detail-card" style={{ marginBottom: 12 }}>
            <div className="card-header">
              <div className="card-title">Dados da filantropia</div>
            </div>
            <dl className="detail-fields">
              <div className="span-2">
                <dt>Descrição</dt>
                <dd>{item.description || '—'}</dd>
              </div>
              <div>
                <dt>Categoria</dt>
                <dd>{philanthropyCategoryLabel(item.category)}</dd>
              </div>
              <div>
                <dt>Última doação</dt>
                <dd className="mono">
                  {item.donationDate ? formatDateOnly(item.donationDate) : '—'}
                </dd>
              </div>
              <div>
                <dt>Criado em</dt>
                <dd className="mono">
                  {item.createdAt
                    ? new Date(item.createdAt).toLocaleDateString('pt-BR')
                    : '—'}
                </dd>
              </div>
              <div>
                <dt>Valor disponível</dt>
                <dd>{formatCurrency(available)}</dd>
              </div>
              <div>
                <dt>Valor doado</dt>
                <dd>{formatCurrency(donated)}</dd>
              </div>
            </dl>
          </div>
        </>
      ) : (
        <div className="card">
          <div className="card-header">
            <div className="card-title">Lançamentos de doações</div>
            <span className="muted">{donations.length} registros</span>
          </div>
          <div style={{ padding: '12px 20px 20px' }}>
            <div
              style={{
                display: 'grid',
                gridTemplateColumns: '1fr 1fr 1.4fr auto',
                gap: 10,
                marginBottom: 16,
              }}
              className="donation-form-row"
            >
              <label className="filter-field">
                <span className="filter-label">Data *</span>
                <input
                  className="input"
                  type="date"
                  value={donationDate}
                  onChange={e => setDonationDate(e.target.value)}
                />
              </label>
              <label className="filter-field">
                <span className="filter-label">Valor *</span>
                <input
                  className="input"
                  type="text"
                  value={donationAmountDisplay}
                  onChange={e => setDonationAmountDisplay(applyCurrencyMask(e.target.value))}
                  placeholder="R$ 0,00"
                  inputMode="decimal"
                />
              </label>
              <label className="filter-field">
                <span className="filter-label">Descrição</span>
                <input
                  className="input"
                  type="text"
                  value={donationDescription}
                  onChange={e => setDonationDescription(e.target.value)}
                />
              </label>
              <div style={{ alignSelf: 'end' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={savingDonation}
                  onClick={async () => {
                    if (!id || !donationDate || !donationAmountDisplay) return;
                    setSavingDonation(true);
                    try {
                      const amount = parseCurrency(donationAmountDisplay);
                      const updated = await PhilanthropiesAPI.addDonation(id, {
                        date: donationDate,
                        amount,
                        description: donationDescription || null,
                      });
                      setItem(updated);
                      setDonations(await PhilanthropiesAPI.listDonations(id));
                      queryClient.invalidateQueries(['philanthropies']);
                      setDonationDate('');
                      setDonationAmountDisplay('');
                      setDonationDescription('');
                    } finally {
                      setSavingDonation(false);
                    }
                  }}
                >
                  {savingDonation ? 'Salvando…' : 'Adicionar'}
                </button>
              </div>
            </div>

            {donations.length === 0 ? (
              <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
                Nenhum lançamento de doação ainda.
              </div>
            ) : (
              <div className="table-wrap">
                <table className="data-table">
                  <thead>
                    <tr>
                      <th>Data</th>
                      <th>Descrição</th>
                      <th style={{ textAlign: 'right' }}>Valor</th>
                      <th style={{ textAlign: 'right' }}>Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {donations.map(d => (
                      <tr key={d.id}>
                        {editingId === d.id ? (
                          <>
                            <td>
                              <input
                                className="input"
                                type="date"
                                value={editingDate}
                                onChange={e => setEditingDate(e.target.value)}
                              />
                            </td>
                            <td>
                              <input
                                className="input"
                                type="text"
                                value={editingDescription}
                                onChange={e => setEditingDescription(e.target.value)}
                              />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <input
                                className="input"
                                type="text"
                                value={editingAmountDisplay}
                                onChange={e =>
                                  setEditingAmountDisplay(applyCurrencyMask(e.target.value))
                                }
                                style={{ textAlign: 'right' }}
                              />
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="row-actions">
                                <button
                                  type="button"
                                  className="link-btn"
                                  disabled={savingEdit}
                                  onClick={async () => {
                                    if (!id || !editingId) return;
                                    setSavingEdit(true);
                                    try {
                                      const amount = parseCurrency(editingAmountDisplay);
                                      const updatedParent = await PhilanthropiesAPI.updateDonation(
                                        id,
                                        editingId,
                                        {
                                          date: editingDate || d.date,
                                          amount,
                                          description: editingDescription,
                                        }
                                      );
                                      setItem(updatedParent);
                                      setDonations(await PhilanthropiesAPI.listDonations(id));
                                      queryClient.invalidateQueries(['philanthropies']);
                                      setEditingId(null);
                                    } finally {
                                      setSavingEdit(false);
                                    }
                                  }}
                                >
                                  Salvar
                                </button>
                                <button
                                  type="button"
                                  className="link-btn action-secondary"
                                  onClick={() => setEditingId(null)}
                                >
                                  Cancelar
                                </button>
                              </div>
                            </td>
                          </>
                        ) : (
                          <>
                            <td className="mono">{formatDateOnly(d.date)}</td>
                            <td>{d.description || '—'}</td>
                            <td className="amount" style={{ textAlign: 'right' }}>
                              {formatCurrency(Number(d.amount) || 0)}
                            </td>
                            <td style={{ textAlign: 'right' }}>
                              <div className="row-actions">
                                <button
                                  type="button"
                                  className="link-btn"
                                  onClick={() => {
                                    setEditingId(d.id);
                                    setEditingDate(toDateOnlyString(d.date));
                                    setEditingAmountDisplay(
                                      formatCurrencyInput(Number(d.amount || 0))
                                    );
                                    setEditingDescription(d.description || '');
                                  }}
                                >
                                  Editar
                                </button>
                                <button
                                  type="button"
                                  className="link-btn action-keep action-danger"
                                  onClick={async () => {
                                    if (!id) return;
                                    const updatedParent = await PhilanthropiesAPI.deleteDonation(
                                      id,
                                      d.id
                                    );
                                    setItem(updatedParent);
                                    setDonations(await PhilanthropiesAPI.listDonations(id));
                                    queryClient.invalidateQueries(['philanthropies']);
                                  }}
                                >
                                  Excluir
                                </button>
                              </div>
                            </td>
                          </>
                        )}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      )}

      <ConfirmationModal
        isOpen={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        title="Excluir filantropia"
        message="Tem certeza que deseja excluir este registro? Esta ação não pode ser desfeita."
        onConfirm={async () => {
          if (!id) return;
          setDeleting(true);
          try {
            await PhilanthropiesAPI.remove(id);
            queryClient.invalidateQueries(['philanthropies']);
            navigate('/philanthropies');
          } finally {
            setDeleting(false);
            setShowDeleteModal(false);
          }
        }}
        confirmText="Excluir"
        cancelText="Cancelar"
        type="danger"
        isLoading={deleting}
      />
    </DetailShell>
  );
};

export default PhilanthropyDetail;
